import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import usePrevious from '@rio-cloud/rio-uikit/usePrevious';
import Fade, { type FadeAnimationStyle } from '@rio-cloud/rio-uikit/Fade';
import useOnScreen from '@rio-cloud/rio-uikit/hooks/useOnScreen';

import DefaultRedirect from '../../routes/DefaultRedirect';
import RouteUpdater from '../../routes/RouteUpdater';
import { MessageHandler } from '../widgets/MessageHandler';
import { SendbirdProvider } from '../../sendbird/useSendbirdApi';
import { useChatConnection } from '../../sendbird/hooks/useChatConnection';
import { BroadcastViewPage } from '../../pages/BroadcastViewPage';
import { ChatListViewPage } from '../../pages/ChatListViewPage';
import { FeedbackPage } from '../../pages/FeedbackPage';
import {
    BROADCAST_ROUTE,
    CHAT_DETAILS_ROUTE,
    DEFAULT_ROUTE,
    FEEDBACK_ROUTE,
    HELP_ROUTE,
    NEW_CHAT_ROUTE,
} from '../../routes/Router';
import { MessageViewPage } from '../../pages/MessageViewPage';
import { NewChatViewPage } from '../../pages/NewChatViewPage';
import { chatVisibilityChanged } from './chatSlice';
import { HelpPage } from '../../pages/HelpPage';
import { gaPush, TRACKING_CATEGORIES } from '../../configuration/setup/googleAnalytics';

const ChatView = ({
    animationStyle,
    currentLocation,
}: {
    animationStyle: FadeAnimationStyle;
    currentLocation: string;
}) => {
    const dispatch = useDispatch();
    const chatConnection = useChatConnection();

    const { pathname } = useLocation();
    const previousLocation = usePrevious(pathname);
    const hasDifferentRoute = previousLocation !== pathname;

    const appRef = useRef<HTMLDivElement>(null);
    const onScreen = useOnScreen(appRef);
    useEffect(() => {
        dispatch(chatVisibilityChanged(onScreen));
        if (onScreen) {
            gaPush({
                category: TRACKING_CATEGORIES.CHAT,
                action: 'Show chat',
                label: 'Show chat view',
            });
        }
    }, [onScreen]);

    return (
        <div ref={appRef}>
            <SendbirdProvider value={chatConnection}>
                <Fade
                    initial={false}
                    exitBeforeEnter
                    animationStyle={hasDifferentRoute ? animationStyle : 'fade'}
                    duration={0.25}
                    motionProps={{ transition: { duration: 0.25, type: 'spring' } }}
                >
                    {currentLocation === DEFAULT_ROUTE && <ChatListViewPage key='chatListViewPage' />}
                    {currentLocation === NEW_CHAT_ROUTE && <NewChatViewPage key='newChatViewPage' />}
                    {currentLocation === BROADCAST_ROUTE && <BroadcastViewPage key='broadcastViewPage' />}
                    {currentLocation === CHAT_DETAILS_ROUTE && <MessageViewPage key='messageViewPage' />}
                    {currentLocation === FEEDBACK_ROUTE && <FeedbackPage key='feedbackPage' />}
                    {currentLocation === HELP_ROUTE && <HelpPage key='helpPage' />}
                    <div />
                </Fade>
                <RouteUpdater />
                <DefaultRedirect />
                <MessageHandler />
            </SendbirdProvider>
        </div>
    );
};

export default ChatView;
